import {
  createTenant,
  encryptWithPublicKey,
  fetchTenant,
  payAndMoveIn,
  payAndReserve,
  tenantSignIn,
  tenantSignUp,
  updateTenant,
} from '@codeparva/v11-tfw-api-library';

import { thankYouPageUrl } from './config';
import { navigate } from 'gatsby';
import { updatedDateFormat } from './GenericFormHelper';
import {
  addTenantCookie,
  checkMoveInReserveFailedCookie,
  deleteTenantCookie,
  getCookies,
  setMoveInReserveCookie,
} from './cookiesHelper';
import { getMyOrigin } from '../../../../../utilities/origin';

export const callBackHandler = async (
  paymentData: any,
  otherData: any,
  facilityId?: string,
  individualUnitDataContext?: any,
  _gatsbyPathName?: string,
  paymentComponentReq?: any,
  tenantToken?: string
) => {
  const errorCookie = checkMoveInReserveFailedCookie();
  if (!errorCookie) {
    let receipt_url = '';
    const { reserve } = paymentComponentReq || {};
    const {
      data: { ledger_id = '' } = {},
      reservationConfigs,
      moveInConfigs,
    } = otherData;
    const { isReservation, isMoveIn } = reservationConfigs;
    const {
      tenantUserName,
      tenantPassword,
      tenantId,
      tenantIdFromCreationAPI,
      individualUnitData,
      ...filteredPaymentData
    } = paymentData;
    let requestBodyReservation: any = {
      ledger_id: ledger_id || reservationConfigs.data.ledger_id,
      payment: filteredPaymentData,
      reserve: reserve || reservationConfigs.reservation,
    };
    if (Object.keys(paymentData).length === 0) {
      const { payment, ...rest } = requestBodyReservation;
      requestBodyReservation = rest;
    }

    const normalRequestBodyMoveIn = {
      ledger_id: ledger_id || moveInConfigs.data.ledger_id,
      payment: filteredPaymentData,
      move_in: otherData.moveIn || moveInConfigs.move_in,
    };

    const bevMoveIn = {
      ...normalRequestBodyMoveIn.move_in,
      unit_id: individualUnitData?.selectedUnitData
        ? individualUnitData.selectedUnitData.selectedUnitId
        : '',
    };

    const bevRequestBodyMoveIn = {
      ...normalRequestBodyMoveIn,
      move_in: bevMoveIn,
    };

    const requestBodyMoveIn =
      individualUnitData?.selectedUnitData &&
      individualUnitData.selectedUnitData.show
        ? bevRequestBodyMoveIn
        : normalRequestBodyMoveIn;

    const {
      move_in: { rental_info: { unit_rate = '', period = '' } = {} } = {},
    } = moveInConfigs ?? {};

    const paymentApiToCall = isReservation ? payAndReserve : payAndMoveIn;
    const paymentReqBody = isReservation
      ? requestBodyReservation
      : requestBodyMoveIn;

    const [
      paymentApiErr,
      {
        data: {
          success = false,
          data: [payAndReserveResponseData = {}] = [],
          data: payAndMoveInResponseData = {},
          relational_data: relationalData = {},
        } = {},
      } = {},
    ] = await paymentApiToCall({
      paymentReq: paymentReqBody,
      facilityId: facilityId || otherData.facilityId,
      tenantToken,
    });
    if (paymentApiErr) {
      console.log('API Error', paymentApiErr);
      setMoveInReserveCookie();
      throw new Error('API Error');
    }

    if (success) {
      const navigateToThankyouPage = () => {
        const requestProps = isMoveIn ? otherData.move_in : reserve;
        const { rental_info: { move_in_date = '' } = {} } = requestProps ?? {};
        const getResevationData = () => {
          if (isReservation) {
            const {
              reservation_code,
              rental_info: {
                period,
                unit_rate,
                reservation_deposit,
                move_in_date,
              },
            } = payAndReserveResponseData;

            return {
              'Confirmation Number': reservation_code,
              Period: period,
              'Unit Rate': unit_rate || '',
              'Amount Paid': reservation_deposit,
              'Move In Date': updatedDateFormat(move_in_date),
              'Unit Type':
                individualUnitDataContext?.unitTypeName ||
                paymentData?.individualUnitData?.unitTypeName,
            };
          } else {
            return {
              'Confirmation Number': '',
              Period: '',
              'Unit Rate': unit_rate || '',
              'Amount Paid': '',
              'Move In Date': '',
              'Unit Type':
                individualUnitDataContext?.unitTypeName ||
                paymentData?.individualUnitData?.unitTypeName,
            };
          }
        };
        const {
          current_charges: { total_amount = '' } = {},
          access_code,
          receipt_number,
          esign_token,
        } = payAndMoveInResponseData;

        if (isReservation) {
          const { receipt_url: response_receipt_url = '' } = relationalData;
          receipt_url = response_receipt_url;
        } else {
          const {
            receipt_url: response_receipt_url = '',
          } = payAndMoveInResponseData;
          receipt_url = response_receipt_url;
        }

        const isUnitIdAvailable =
          individualUnitData?.selectedUnitData &&
          individualUnitData.selectedUnitData.show;

        // This navigates to a diff page
        navigate(`/${thankYouPageUrl}`, {
          replace: true,
          state: {
            path: isMoveIn ? 'move-in' : 'reserve',
            eSignToken: esign_token,
            receipt_url,
            movein: {
              'Confirmation Number': access_code,
              Period: period,
              'Transaction Number': receipt_number || '',
              'Unit Rate': unit_rate || '',
              'Amount Paid': total_amount,
              'Move In Date': move_in_date || updatedDateFormat(new Date()),
              'Unit Type':
                individualUnitDataContext?.unitTypeName ||
                paymentData.individualUnitData?.unitTypeName,
              ...(isUnitIdAvailable &&
                isMoveIn && {
                  'Unit Id':
                    individualUnitDataContext?.selectedUnitData
                      .selectedUnitId ||
                    paymentData.individualUnitData?.selectedUnitData
                      .selectedUnitId,
                }),
            },
            reservation: getResevationData(),
          },
        });
      };
      setMoveInReserveCookie('isMoveInReserveSuccess');
      navigateToThankyouPage();
    }
  }
};

export async function makeNoPaymentReservation(
  paymentReq: any,
  tenantToken: string | undefined,
  facilityId: string | number
) {
  const noPaymentRequestBody = {
    ledger_id: paymentReq.ledgerIdFromSMS,
    reserve: {
      unit_type_id: paymentReq?.reserve?.unit_type_id,
      rental_info: paymentReq?.reserve?.rental_info,
    },
  };

  const [
    ,
    {
      data: {
        success = false,
        data: [payAndReserveResData = {}] = [],
        relational_data: { receipt_url = '' } = {},
      } = {},
    } = {},
  ] = await payAndReserve({
    paymentReq: noPaymentRequestBody,
    facilityId,
    tenantToken,
  });

  if (success) {
    deleteTenantCookie('isTenantUpdated=');
    deleteTenantCookie('ledgerInfo=');
    deleteTenantCookie('isNewTenant=');
    navigate(`/${thankYouPageUrl}`, {
      replace: true,
      state: {
        error: '',
        path: 'reserve',
        eSignToken: '',
        receipt_url,
        movein: {},
        reservation: {
          'Confirmation Number': payAndReserveResData?.reservation_code || '',
          Period:
            payAndReserveResData?.rental_info?.period ||
            paymentReq.reserve.rental_info.period,
          'Unit Rate':
            payAndReserveResData?.rental_info?.unit_rate ||
            paymentReq.reserve.rental_info.unit_rate,
          'Amount Paid':
            payAndReserveResData?.rental_info?.reservation_deposit ||
            paymentReq.reserve.rental_info.reservation_deposit,
          'Move In Date': payAndReserveResData?.rental_info?.move_in_date
            ? updatedDateFormat(payAndReserveResData?.rental_info?.move_in_date)
            : updatedDateFormat(paymentReq.reserve.rental_info.move_in_date),
          'Unit Type': '',
        },
      },
    });
    setMoveInReserveCookie('isMoveInReserveSuccess');
  }
}

export const tenantCreationHelper = async (
  TenantInfo_DriverLicenseNumber: string,
  globalEncKey: string,
  tenantCreateRequest: any,
  ledgerInfo: any,
  facilityID: string,
  tenantId: string,
  setShowError: React.Dispatch<React.SetStateAction<boolean>>,
  setErrors: React.Dispatch<React.SetStateAction<any[]>>,
  values: any,
  globalContext: any,
  sessionTimerContext: any,
  setIsAccountCreated: any,
  ledgerIdFromReservation: any,
  facPublicKey: string
) => {
  const {
    OtherInfo_EmailAddress: tenantUserName,
    OtherInfo_NewPassword: tenantUserPassword,
  } = values;

  const encryptDLHelper = async (tenantCreateRequest: any) => {
    const { id_proof: { ssn = '', state_id = '' } = {} } =
      tenantCreateRequest || {};
    const encryptId = async (idType: string, value: string) => {
      if (value) {
        const encryptedValue = await encryptWithPublicKey(
          value,
          globalEncKey,
          true
        );
        tenantCreateRequest = {
          ...tenantCreateRequest,
          id_proof: {
            ...tenantCreateRequest.id_proof,
            [idType]: encryptedValue,
          },
        };
      }
    };
    if (TenantInfo_DriverLicenseNumber)
      await encryptId('driver_license', TenantInfo_DriverLicenseNumber);
    if (ssn) await encryptId('ssn', ssn);
    if (state_id) await encryptId('state_id', state_id);
    return tenantCreateRequest;
  };

  const tenantCookie = getCookies('isNewTenant');
  const tenantIdFromCookie = tenantCookie.split('&')[2];
  const tenantToken = globalContext?.token;

  if ((ledgerInfo && ledgerInfo.length > 0) || tenantIdFromCookie || tenantId) {
    // Update tenant
    const [
      tenantSearchErr,
      {
        data: {
          success = false,
          data: tenantList = [],
          data: [tenantData = {}] = [],
        } = {},
      } = {},
    ] = await fetchTenant({
      termsFilter: {
        id: [tenantIdFromCookie ? tenantIdFromCookie : tenantId],
      },
      facilityId: facilityID,
      tenantToken,
      facPublicKey,
    });
    if (tenantSearchErr) {
      setShowError(true);
      setErrors(prevState => [...prevState, tenantSearchErr]);
      console.error('Error in tenant search');
      return [tenantSearchErr, {}];
    }

    if (success) {
      if (tenantList.length > 0) {
        const ledgerDataLength = tenantData.ledger.length;
        const tenantUpdateRequest = await encryptDLHelper(tenantCreateRequest);
        tenantUpdateRequest.tenantId = tenantIdFromCookie
          ? tenantIdFromCookie
          : tenantId;
        if (
          tenantUpdateRequest.phone !== undefined &&
          tenantUpdateRequest.phone.primary_number !== undefined
        ) {
          if (tenantData.phone && Object.keys(tenantData.phone)?.length > 0) {
            tenantUpdateRequest.phone.phone_number_list = [
              getPhoneFieldRequestBody(
                tenantUpdateRequest.phone.primary_number.slice(1, 4),
                tenantUpdateRequest.phone.primary_number.slice(
                  4,
                  tenantUpdateRequest.phone.primary_number.length
                ),
                '1',
                'us',
                'Update',
                'Cell_Phone',
                1
              ),
            ];
          } else {
            tenantUpdateRequest.phone.phone_number_list = [
              getPhoneFieldRequestBody(
                tenantUpdateRequest.phone.primary_number.slice(1, 4),
                tenantUpdateRequest.phone.primary_number.slice(
                  4,
                  tenantUpdateRequest.phone.primary_number.length
                ),
                '1',
                'us',
                'Create',
                'Cell_Phone'
              ),
            ];
          }
        }
        const {
          ledger,
          tenantId: tenantIdInUpdateRequest,
          ...restTenantUpdateRequest
        } = tenantUpdateRequest;

        if (!ledgerIdFromReservation) {
          restTenantUpdateRequest.ledger = [
            {
              label: `Ledger${ledgerDataLength + 1}`,
              __action_type: 'Create',
            },
          ];
        }

        const [updateTenantErr, updateTenantRes] = await updateTenant({
          updateTenantReq: restTenantUpdateRequest,
          tenantId,
          facilityId: facilityID,
          tenantToken,
        });
        if (updateTenantErr) {
          setShowError(true);
          setErrors(prevState => [...prevState, updateTenantErr]);
          console.error('Error in tenant search/update');
          return [updateTenantErr, {}];
        }

        document.cookie =
          'isTenantUpdated=' +
          Buffer.from(
            `${facilityID}&${getMyOrigin()}&${tenantUpdateRequest.tenantId}`
          ).toString('base64');

        return [null, updateTenantRes];
      }
    }
  } else {
    // Create tenant
    const tenantCreateReq = await encryptDLHelper(tenantCreateRequest);

    const [createTenantErr, createTenantRes = {}] = await createTenant({
      createTenantReq: tenantCreateReq,
      facilityId: facilityID,
      facPublicKey,
    });
    if (createTenantErr) {
      console.error('Create tenant error');
      setShowError(true);
      setErrors(prevState => [...prevState, createTenantErr]);
      return [createTenantErr, {}];
    }

    const {
      data: {
        success: createTenantSuccessful = false,
        data: [{ id = '', name = {} } = {}] = [],
      } = {},
    } = createTenantRes;

    if (createTenantSuccessful) {
      addTenantCookie(facilityID, id, 'isNewTenant=');
      const encryptedPassword = await encryptWithPublicKey(
        tenantUserPassword,
        globalEncKey
      );
      if (id !== '') {
        const [
          signUpErr,
          { data: { success: signUpSuccessful = false } = {} } = {},
        ] = await tenantSignUp({
          tenantSignUpReq: {
            username: tenantUserName,
            password: encryptedPassword,
          },
          facilityId: facilityID,
          tenantId: id,
          facPublicKey,
        });
        if (signUpErr) {
          console.error('Sign-up error');
          setShowError(true);
          setErrors(prevState => [...prevState, signUpErr]);
          return [signUpErr, {}];
        }

        if (signUpSuccessful) {
          const [
            signInErr,
            {
              data: {
                data: {
                  token: tenantToken = '',
                  token_expiry: tokenExpiry = '',
                } = {},
              } = {},
            } = {},
          ] = await tenantSignIn({
            tenantSignInReq: {
              username: tenantUserName,
              password: encryptedPassword,
            },
            facilityId: facilityID,
            facPublicKey,
          });
          if (signInErr) {
            console.error('Sign-in error');
            setShowError(true);
            setErrors(prevState => [...prevState, signInErr]);
            return [signInErr, {}];
          }

          if (tenantToken) {
            const expires = globalContext?.expires;
            globalContext?.setCToken(tenantToken, {
              path: '/',
              expires,
            });
            globalContext?.setCTokenExpiry(tokenExpiry, {
              path: '/',
              expires,
            });
            globalContext?.setCUserName(tenantUserName, {
              path: '/',
              expires,
            });
            globalContext?.setCExpiryTime(expires.toISOString(), {
              path: '/',
              expires,
            });
            globalContext?.setLoggedInSmsFacilityId(facilityID);
            globalContext?.setCTenantId(facilityID + '_' + id, {
              path: '/',
              expires,
            });
            globalContext?.setToken(tenantToken);
            globalContext?.setTokenExpiry(tokenExpiry);
            globalContext?.setUsername(tenantUserName);
            sessionTimerContext?.setSessionExpiryTime(expires.toISOString());
            globalContext?.setTenantId(id);
            globalContext?.setCTenantName(name, { path: '/', expires });
            globalContext?.setTenantName(name);
          }
          setIsAccountCreated(true);
        }
      }
      return [null, createTenantRes];
    }
  }
  return ['Error', {}];
};

export const getPhoneFieldRequestBody = (
  area_code: string,
  number: string,
  countryCode: string,
  country: string,
  actionType: string,
  type: string,
  phoneId?: number
) => {
  return {
    type,
    phone: {
      country_code: countryCode,
      area_code,
      country,
      number,
    },
    __action_type: actionType,
    ...(phoneId && { phone_id: phoneId }),
  };
};

export const getAlternateContactFieldRequestBody = (
  area_code: string,
  number: string,
  country_code: string,
  country: string
) => {
  return {
    area_code,
    country_code,
    number,
    country,
  };
};

export const callDeactivateLedgerCall = async (
  tenantId: any,
  facilityId: any,
  ledgerIdFromSMS: any,
  tenantToken: string
) => {
  return await updateTenant({
    updateTenantReq: {
      tenantId: tenantId,
      ledger: [
        {
          id: ledgerIdFromSMS,
          __action_type: 'Deactivate',
        },
      ],
    },
    tenantId,
    facilityId,
    tenantToken,
  });
};
